import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaCalendar, FaClock, FaVideo } from 'react-icons/fa';
import axios from 'axios';

const ZohoMeetingModal = ({ show, onHide, userId, userName }) => {
  const [formData, setFormData] = useState({
    meetingTitle: '',
    meetingAgenda: '',
    meetingDate: '',
    meetingTime: '',
    duration: 60
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show) {
      // Reset form when modal opens
      setFormData({
        meetingTitle: '',
        meetingAgenda: '',
        meetingDate: '',
        meetingTime: '',
        duration: 60
      });
      setError('');
      setSuccess('');
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/zoho-meetings/schedule`,
        {
          userId,
          ...formData
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Meeting scheduled successfully! User will receive an email notification.');
        setTimeout(() => {
          onHide();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaVideo className="me-2" />
          Schedule Zoho Meeting for {userName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Meeting Title *</Form.Label>
            <Form.Control
              type="text"
              name="meetingTitle"
              value={formData.meetingTitle}
              onChange={handleChange}
              placeholder="Enter meeting title"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Meeting Agenda</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="meetingAgenda"
              value={formData.meetingAgenda}
              onChange={handleChange}
              placeholder="Enter meeting agenda (optional)"
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaCalendar className="me-2" />
                  Meeting Date *
                </Form.Label>
                <Form.Control
                  type="date"
                  name="meetingDate"
                  value={formData.meetingDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaClock className="me-2" />
                  Meeting Time *
                </Form.Label>
                <Form.Control
                  type="time"
                  name="meetingTime"
                  value={formData.meetingTime}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Duration (minutes)</Form.Label>
            <Form.Select
              name="duration"
              value={formData.duration}
              onChange={handleChange}
            >
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </Form.Select>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Scheduling...
                </>
              ) : (
                'Schedule Meeting'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ZohoMeetingModal;
